'use client'

import { useState, useMemo } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import type { Car } from '@garage/shared'
import { clientDisplayName, carLabel, FUEL_TYPE_LABELS, whatsappNumberFor, toWaMeDigits, isCompany } from '@garage/shared'
import {
  PageHeader,
  Button,
  IconButton,
  ConfirmDialog,
  EmptyState,
  Skeleton,
  useSnackbar,
  KeyValue,
  EditIcon,
  DeleteIcon,
  AddIcon,
  WhatsappIcon,
  PeopleIcon,
  ReceiptIcon,
  DownloadIcon,
} from '@/ui'
import { StatusChip } from '@/components/StatusChip.js'
import { ClientFormDialog } from '@/components/ClientFormDialog.js'
import { CarFormDialog } from '@/components/CarFormDialog.js'
import { ActivityInfo } from '@/components/ActivityInfo.js'
import { api, ApiError } from '@/lib/api.js'
import { qk } from '@/lib/query-keys.js'
import { useFormatters, useFeatures } from '@/lib/hooks.js'

const OTHER_GROUP = 'Other / manually entered'

export function ClientDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const snackbar = useSnackbar()
  const formatters = useFormatters()
  const features = useFeatures()
  const { t } = useTranslation()

  const [editingCar, setEditingCar] = useState<Car | undefined>()
  const [carFormOpen, setCarFormOpen] = useState(false)
  const [deleteCarId, setDeleteCarId] = useState<string | null>(null)
  const [clientFormOpen, setClientFormOpen] = useState(false)

  const { data: client, isLoading, isError } = useQuery({
    queryKey: qk.client(id ?? ''),
    queryFn: () => api.clients.get(id ?? ''),
    enabled: !!id,
  })

  const deleteCar = useMutation({
    mutationFn: (carId: string) => api.cars.remove(carId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qk.client(id ?? '') })
      queryClient.invalidateQueries({ queryKey: qk.dashboard })
      snackbar.success(t('clientDetail.carDeleted'))
      setDeleteCarId(null)
    },
    onError: (error) => {
      snackbar.error(
        error instanceof ApiError ? error.message : t('clientDetail.failedToDeleteCar')
      )
    },
  })

  const handleEditCar = (car: Car) => {
    setEditingCar(car)
    setCarFormOpen(true)
  }

  const handleCarFormClose = () => {
    setCarFormOpen(false)
    setEditingCar(undefined)
  }

  const handleAddCar = () => {
    setEditingCar(undefined)
    setCarFormOpen(true)
  }

  // Group jobcards by car record (or "Other" for manually entered cars). Plate
  // alone can't be the key: cars without a plate would all collapse into one group.
  const groupedJobcards = useMemo(() => {
    if (!client) return {}

    const carIds = new Set(client.cars.map((c) => c.id))
    const carIdByPlate = new Map(client.cars.filter((c) => c.plate).map((c) => [c.plate, c.id]))
    const groups: Record<string, typeof client.jobcards> = {}

    client.jobcards.forEach((jc) => {
      const group =
        jc.carId && carIds.has(jc.carId)
          ? jc.carId
          : (jc.plate && carIdByPlate.get(jc.plate)) || OTHER_GROUP
      if (!groups[group]) groups[group] = []
      groups[group].push(jc)
    })

    return groups
  }, [client])

  if (isLoading) {
    return (
      <div className="space-y-8">
        <Skeleton lines={2} />
        <Skeleton lines={4} />
        <Skeleton lines={6} />
      </div>
    )
  }

  if (isError || !client) {
    return (
      <EmptyState
        icon={<PeopleIcon className="w-12 h-12 text-on-surface-variant" />}
        title={t('clientDetail.notFound')}
        action={
          <Button variant="tonal" onClick={() => navigate('/clients')}>
            {t('clientDetail.backToClients')}
          </Button>
        }
      />
    )
  }

  const waDigits = toWaMeDigits(whatsappNumberFor(client))

  return (
    <div className="space-y-8">
      <PageHeader
        title={clientDisplayName(client)}
        back={{ to: '/clients', label: t('clients.title') }}
        actions={
          <div className="flex gap-2">
            <Button
              variant="outlined"
              icon={<ReceiptIcon className="w-5 h-5" />}
              onClick={() => window.open(`/clients/${id}/statement`, '_blank')}
            >
              {t('clientDetail.statementOfAccount')}
            </Button>
            {client.jobcards.some((jc) => jc.status === 'invoiced') && (
              <Button
                variant="outlined"
                icon={<DownloadIcon className="w-5 h-5" />}
                onClick={() => {
                  window.location.href = api.clients.unpaidInvoicesUrl(client.id)
                }}
              >
                {t('clientDetail.downloadUnpaidInvoices', 'Download unpaid invoices')}
              </Button>
            )}
            <Button
              variant="outlined"
              icon={<EditIcon className="w-5 h-5" />}
              onClick={() => setClientFormOpen(true)}
            >
              {t('common.edit')}
            </Button>
            {features.whatsappEnabled && (
              <Button
                variant="filled"
                icon={<WhatsappIcon className="w-5 h-5" />}
                onClick={() => {
                  window.open(`https://wa.me/${waDigits}`, '_blank')
                }}
              >
                WhatsApp
              </Button>
            )}
          </div>
        }
      />

      <ActivityInfo entityType="client" entityId={id!} />

      {/* Contact details panel */}
      <div className="bg-surface-container border border-outline-variant rounded-lg p-5">
        <div className="space-y-3">
          {isCompany(client) && (
            <>
              {client.trn && (
                <KeyValue
                  label={t('clientDetail.taxRegistrationNumber')}
                  value={<span className="ref">{client.trn}</span>}
                  numeric={false}
                />
              )}
              {client.tradeLicence && (
                <KeyValue
                  label={t('clientDetail.tradeLicence')}
                  value={client.tradeLicence}
                  numeric={false}
                />
              )}
              {client.billingAddress && (
                <KeyValue
                  label={t('clientDetail.billingAddress')}
                  value={<span className="whitespace-pre-wrap">{client.billingAddress}</span>}
                  numeric={false}
                />
              )}
            </>
          )}
          <div className="flex items-center justify-between">
            <span className="text-body-sm text-on-surface-variant">{t('clients.columns.phone')}</span>
            <span className="ref text-body-md">{client.phone}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-body-sm text-on-surface-variant">{t('clients.columns.whatsapp')}</span>
            <span className="text-body-md">
              {client.whatsappPhone ? (
                <span className="ref">{client.whatsappPhone}</span>
              ) : (
                <span className="text-on-surface-variant">{t('clients.sameAsPhone')}</span>
              )}
            </span>
          </div>
          {client.notes && (
            <div className="pt-3 border-t border-outline-variant">
              <span className="text-body-sm text-on-surface-variant block mb-2">{t('clientDetail.notes')}</span>
              <span className="text-body-md whitespace-pre-wrap text-on-surface">
                {client.notes}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Cars panel */}
      <div className="bg-surface-container border border-outline-variant rounded-lg p-5">
        <div className="space-y-4">
          <h2 className="text-title-md text-on-surface">{t('clientDetail.cars')}</h2>
          {client.cars.length === 0 ? (
            <EmptyState
              title={t('clientDetail.noCarsYet')}
              description={t('clientDetail.addFirstCar')}
            />
          ) : (
            <div className="space-y-0">
              {client.cars.map((car, index) => (
                <div
                  key={car.id}
                  className={`flex items-start justify-between gap-4 py-4 ${
                    index < client.cars.length - 1 ? 'border-b border-outline-variant' : ''
                  }`}
                >
                  <div className="flex-1 min-w-0">
                    <h3 className="text-body-md text-on-surface">
                      {(() => {
                        const label = carLabel(car)
                        const plateSeparator = ' — '
                        const [mainPart, plate] = label.split(plateSeparator)
                        return (
                          <>
                            {mainPart} <span className="ref">{plateSeparator}{plate}</span>
                          </>
                        )
                      })()}
                    </h3>
                    <div className="space-y-1 mt-1">
                      {car.color && (
                        <p className="text-body-sm text-on-surface-variant">
                          {car.color}
                        </p>
                      )}
                      {car.vin && (
                        <p className="text-body-sm text-on-surface-variant">
                          VIN: <span className="ref">{car.vin}</span>
                        </p>
                      )}
                      {car.engineSize && (
                        <p className="text-body-sm text-on-surface-variant">
                          {t('clientDetail.engine')}: {car.engineSize}
                        </p>
                      )}
                      {car.fuelType && (
                        <p className="text-body-sm text-on-surface-variant">
                          {t(`carForm.fuelTypes.${car.fuelType}`, FUEL_TYPE_LABELS[car.fuelType])}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2 flex-shrink-0">
                    <IconButton
                      icon={<EditIcon className="w-5 h-5" />}
                      label={t('clientDetail.editCar')}
                      variant="standard"
                      onClick={() => handleEditCar(car)}
                    />
                    <IconButton
                      icon={<DeleteIcon className="w-5 h-5" />}
                      label={t('clientDetail.deleteCar')}
                      variant="danger"
                      onClick={() => setDeleteCarId(car.id)}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
          <Button
            variant="text"
            size="sm"
            icon={<AddIcon className="w-4 h-4" />}
            onClick={handleAddCar}
          >
            {t('clientDetail.addCar')}
          </Button>
        </div>
      </div>

      {/* Jobcard history */}
      {client.jobcards.length === 0 ? (
        <EmptyState
          title={t('jobcards.noJobcardsYet')}
          description={t('clientDetail.createJobcardToStart')}
        />
      ) : (
        <div className="space-y-6">
          {Object.entries(groupedJobcards)
            .sort(([a], [b]) => {
              // Sort with "Other" at the end
              if (a === OTHER_GROUP) return 1
              if (b === OTHER_GROUP) return -1
              const label = (id: string) => {
                const car = client.cars.find((c) => c.id === id)
                return car ? carLabel(car) : id
              }
              return label(a).localeCompare(label(b))
            })
            .map(([groupKey, jobcards]) => {
              const groupCar = groupKey === OTHER_GROUP ? undefined : client.cars.find((c) => c.id === groupKey)
              const plate = groupCar?.plate ?? ''
              return (
              <div key={groupKey} className="bg-surface-container border border-outline-variant rounded-lg p-5">
                <h3 className="text-title-md text-on-surface mb-4">
                  {groupKey === OTHER_GROUP ? (
                    t('clientDetail.otherManuallyEntered')
                  ) : groupCar && (groupCar.brand || groupCar.model || groupCar.year) ? (
                    // Make, model and year first so cars are told apart at a glance; plate after.
                    <>
                      {carLabel(groupCar).split(' — ')[0]}
                      {plate && <span className="ref"> — {plate}</span>}
                    </>
                  ) : (
                    plate || (groupCar ? carLabel(groupCar) : '')
                  )}
                </h3>
                <div className="space-y-0">
                  {jobcards.map((jc, index) => (
                    <div
                      key={jc.id}
                      className={`flex items-center justify-between gap-4 py-3 interactive cursor-pointer ${
                        index < jobcards.length - 1 ? 'border-b border-outline-variant' : ''
                      }`}
                      onClick={() => navigate(`/jobcards/${jc.id}`)}
                    >
                      <div className="flex-1 min-w-0">
                        <p className="text-body-md text-on-surface">
                          <span className="ref">{jc.number}</span>
                        </p>
                        <p className="text-body-sm text-on-surface-variant">
                          {formatters.date(jc.createdAt)}
                        </p>
                      </div>
                      <StatusChip status={jc.status} />
                      <div className="text-end whitespace-nowrap">
                        <p className="text-body-md text-on-surface tabular">
                          {formatters.money(jc.totalMinor)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              )
            })}
        </div>
      )}

      <Button
        variant="filled"
        icon={<AddIcon className="w-5 h-5" />}
        onClick={() => navigate(`/jobcards/new?clientId=${id}`)}
        fullWidth
      >
        {t('clientDetail.newJobcardForClient')}
      </Button>

      <ClientFormDialog
        open={clientFormOpen}
        client={client}
        onClose={() => setClientFormOpen(false)}
      />

      <CarFormDialog
        open={carFormOpen}
        car={editingCar}
        clientId={id ?? null}
        onClose={handleCarFormClose}
      />

      <ConfirmDialog
        open={!!deleteCarId}
        title={t('clientDetail.deleteCarTitle')}
        message={t('clientDetail.deleteCarMessage')}
        confirmLabel={t('common.delete')}
        cancelLabel={t('common.cancel')}
        destructive
        loading={deleteCar.isPending}
        onConfirm={() => {
          if (deleteCarId) {
            deleteCar.mutate(deleteCarId)
          }
        }}
        onCancel={() => setDeleteCarId(null)}
      />
    </div>
  )
}
